import React from 'react';
import { ImageStyle } from 'react-native';
import { Image, ImageContentFit } from 'expo-image';

interface CachedImageProps {
  source: any; // For require() assets
  style?: ImageStyle;
  resizeMode?: ImageContentFit;
  [key: string]: any; // Allow other props
}

/**
 * CachedImage component that efficiently caches static assets
 * Particularly useful for empty state images and illustrations
 */
const CachedImage = ({ 
  source, 
  style, 
  resizeMode = 'contain',
  ...props 
}: CachedImageProps) => {
  // Use Expo Image for better performance and caching
  return (
    <Image
      source={source}
      style={style}
      contentFit={resizeMode}
      transition={200}
      cachePolicy="memory-disk" // Cache in memory and disk
      {...props}
    />
  );
};

export default CachedImage;

/**
 * Preload commonly used empty state images
 */
export const preloadEmptyStateImages = async () => {
  try {
    console.log('🖼️ Preloading empty state images...');
    
    // List of empty state images to preload
    const imagesToPreload = [
      require('../assets/images/reminder.png'),
      require('../assets/images/notification.png'),
      require('../assets/images/adventure.png'),
      require('../assets/images/taking-notes.png'),
      require('../assets/images/chill-time.png'),
      require('../assets/images/remote-meeting.png'),
    ];

    // Preload images using Expo Image
    const preloadPromises = imagesToPreload.map(async (imageSource) => {
      try {
        await Image.prefetch(imageSource);
      } catch (error) {
        console.warn('⚠️ Failed to preload image:', error);
      }
    });

    await Promise.allSettled(preloadPromises);
    console.log('✅ Empty state images preloaded successfully');
  } catch (error) {
    console.error('❌ Error preloading empty state images:', error);
  }
};

/**
 * Preload "Mais do Padrinho" carousel images for better performance
 */
export const preloadCarouselImages = async () => {
  try {
    console.log('🎠 Preloading "Mais do Padrinho" carousel images...');
    
    // List of carousel images to preload
    const carouselImages = [
      require('../assets/images/community.png'),
      require('../assets/images/podcast.png'), 
      require('../assets/images/blog.png'),
    ];

    // Preload images using Expo Image
    const preloadPromises = carouselImages.map(async (imageSource) => {
      try {
        await Image.prefetch(imageSource);
      } catch (error) {
        console.warn('⚠️ Failed to preload carousel image:', error);
      }
    });

    await Promise.allSettled(preloadPromises);
    console.log('✅ "Mais do Padrinho" carousel images preloaded successfully');
  } catch (error) {
    console.error('❌ Error preloading carousel images:', error);
  }
};